import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, IndianRupee, Clock, ListChecks, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Area, AreaChart } from "recharts";
import api, { fmtINR, shortDate, fmtNum } from "@/lib/api";
import PaidViaWidget from "@/components/PaidViaWidget";

function DeltaBadge({ delta }) {
    // null = last week had zero baseline → no meaningful delta to surface.
    // Showing "+∞%" or "—%" would be noise; just hide the badge entirely.
    if (delta == null || delta === undefined) return null;
    const positive = delta > 0;
    const flat = Math.abs(delta) < 0.05; // round-to-zero — treat as flat
    const cls = flat
        ? "bg-slate-100 text-slate-600"
        : positive
            ? "bg-emerald-50 text-emerald-700"
            : "bg-rose-50 text-rose-700";
    const Icon = flat ? Minus : positive ? TrendingUp : TrendingDown;
    const sign = flat ? "" : positive ? "+" : "";
    return (
        <span data-testid="kpi-delta-badge" className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${cls}`}>
            <Icon className="w-3 h-3" />
            {sign}{flat ? "0" : delta.toFixed(1)}%
        </span>
    );
}

function Stat({ icon: Icon, label, value, hint, color = "blue", delta, deltaSubLabel = "vs last week" }) {
    const map = { blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700",
                  amber: "bg-amber-50 text-amber-700", slate: "bg-slate-100 text-slate-700" };
    return (
        <div className="card p-5 card-hover">
            <div className="flex items-start justify-between">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${map[color]}`}><Icon className="w-4 h-4" /></div>
            </div>
            <div className="text-3xl font-bold mt-2 font-mono tracking-tight">{value}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
                {hint && <div className="text-xs text-slate-500">{hint}</div>}
                {delta !== undefined && <DeltaBadge delta={delta} />}
                {delta !== undefined && delta !== null && (
                    <span className="text-[10px] text-slate-400">{deltaSubLabel}</span>
                )}
            </div>
        </div>
    );
}

export default function PartnerDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/partner/dashboard").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-slate-500">Loading…</div>;
    // Week-over-week deltas — backend computes (this_week vs last_week,
    // trailing 7-day windows). Null when last-week was zero (avoids
    // "+∞%" cards on new accounts).
    const wow = data.weekly_deltas || {};
    const txnDelta = wow.txn_count?.delta_pct;
    const successDelta = wow.success_count?.delta_pct;
    const volumeDelta = wow.success_volume?.delta_pct;
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
                <p className="text-sm text-slate-500 mt-1">Real-time view of your payment flow.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={ListChecks} label="Today's Transactions" value={fmtNum(data.today_count)} hint={`${data.today_success_count} successful`} color="blue" delta={txnDelta} />
                <Stat icon={IndianRupee} label="Today's Volume" value={fmtINR(data.today_volume)} color="emerald" delta={volumeDelta} />
                <Stat icon={Clock} label="Pending" value={fmtNum(data.pending_count)} hint="Awaiting verification" color="amber" />
                <Stat icon={TrendingUp} label="7-day Volume" value={fmtINR(data.weekly_series.reduce((s, x) => s + x.total, 0))} color="slate" delta={volumeDelta} deltaSubLabel="vs prior 7 days" />
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="card p-5 lg:col-span-2">
                    <h3 className="font-semibold mb-3">Last 7 days · successful volume</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.weekly_series}>
                                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.45} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                                <CartesianGrid stroke="#F1F5F9" />
                                <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
                                <YAxis stroke="#94A3B8" fontSize={11} />
                                <Tooltip contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 8 }} formatter={(v) => fmtINR(v)} />
                                <Area type="monotone" dataKey="total" stroke="#2563EB" fill="url(#g)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="card p-5">
                    <h3 className="font-semibold mb-3">Recent activity</h3>
                    <div className="space-y-2">
                        {data.recent_transactions.length === 0 && <div className="text-sm text-slate-400">No transactions yet.</div>}
                        {data.recent_transactions.map((t) => (
                            <div key={t.txn_id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 border-slate-100">
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{t.customer_username}</div>
                                    <div className="text-xs text-slate-500">{shortDate(t.created_at)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono">{fmtINR(t.amount)}</div>
                                    <span className={`pill ${t.status === "success" ? "pill-success" : t.status === "submitted" || t.status === "pending" ? "pill-pending" : "pill-failed"}`}>{t.status}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
                <PaidViaWidget scope="partner" />
                <WebsiteBreakdownWidget />
            </div>
        </div>
    );
}

/**
 * Per-website volume + count breakdown for the partner dashboard.
 *
 * Pulls from `/partner/websites/breakdown?days=7`. Renders one row per
 * website + a synthetic "(no website tagged)" row for legacy / skipped-
 * chooser txns. Sorted by success_volume DESC.
 *
 * Hidden entirely when the partner has zero successful txns AND zero
 * configured websites — keeps the dashboard tidy for new accounts.
 */
function WebsiteBreakdownWidget() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/partner/websites/breakdown", { params: { days: 7 } })
            .then((r) => setData(r.data))
            .catch(() => setData({ items: [] }));
    }, []);
    if (!data) return (
        <div className="card p-5">
            <h3 className="font-semibold mb-3">Volume by website · 7 days</h3>
            <div className="text-sm text-slate-400">Loading…</div>
        </div>
    );
    if (!data.items?.length) return (
        <div className="card p-5" data-testid="website-breakdown-empty">
            <h3 className="font-semibold mb-1">Volume by website · 7 days</h3>
            <div className="text-xs text-slate-500 leading-relaxed">
                No website-tagged payments yet. Add your websites under
                <span className="font-medium"> Partner → Websites</span> to let customers
                self-select which property they&apos;re paying for at checkout.
            </div>
        </div>
    );
    // Compute total for the relative bars + percentages.
    const totalVol = data.items.reduce((s, x) => s + (x.success_volume || 0), 0) || 1;
    const totals = data.totals || {};
    const hasUntagged = (totals.untagged_volume || 0) > 0;
    return (
        <div className="card p-5" data-testid="website-breakdown-card">
            <h3 className="font-semibold mb-3">Volume by website · 7 days</h3>

            {/* Pin-row: aggregate tagged vs untagged. Renders ONLY when
                there's any untagged volume — keeps the dashboard tidy for
                partners who've fully migrated. The amber accent on the
                untagged number nudges the partner to investigate legacy
                links that are bypassing the chooser. */}
            {hasUntagged && (
                <div data-testid="website-breakdown-totals"
                     className="mb-3 pb-3 border-b border-slate-200 grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-violet-50 border border-violet-200 rounded p-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-violet-600 font-semibold">Tagged</div>
                        <div className="font-mono font-bold text-violet-900 text-base mt-0.5">{fmtINR(totals.tagged_volume)}</div>
                        <div className="text-[10px] text-violet-700 mt-0.5">
                            {fmtNum(totals.tagged_count)} txns
                            {totals.tagged_pct != null && <span className="text-violet-500"> · {totals.tagged_pct}%</span>}
                        </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1">
                            Untagged
                            <span className="ml-auto text-amber-500" title="Customers who paid via a legacy link or skipped the chooser. Configure a default website on these links to bring them into the tagged bucket.">⚠</span>
                        </div>
                        <div className="font-mono font-bold text-amber-900 text-base mt-0.5">{fmtINR(totals.untagged_volume)}</div>
                        <div className="text-[10px] text-amber-700 mt-0.5">{fmtNum(totals.untagged_count)} txns</div>
                    </div>
                </div>
            )}

            <div className="space-y-2">
                {data.items.map((w) => {
                    const pct = Math.max(2, Math.round(((w.success_volume || 0) / totalVol) * 100));
                    const isUntagged = !w.website_id;
                    return (
                        <div key={w.website_id || "__none__"} data-testid={`website-breakdown-row-${w.website_id || "none"}`}>
                            <div className="flex items-center justify-between text-sm">
                                <div className={`truncate font-medium max-w-[60%] ${isUntagged ? "text-amber-700 italic" : "text-slate-800"}`}
                                     title={w.name}>{w.name}</div>
                                <div className="text-right text-xs">
                                    <span className="font-mono font-semibold text-slate-900">{fmtINR(w.success_volume)}</span>
                                    <span className="text-slate-400 ml-1.5">· {fmtNum(w.success_count)} txns</span>
                                </div>
                            </div>
                            <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                                <div className={`h-full transition-all ${isUntagged
                                    ? "bg-gradient-to-r from-amber-300 to-amber-500"
                                    : "bg-gradient-to-r from-violet-400 to-violet-600"}`}
                                     style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
